import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Button from '@/components/Button';
import Booking from '@/components/Booking';
import ServiceGallery from '@/components/ServiceGallery';

// Sanitise Galleries
import office_yamaha1 from '@/assets/images/hicom-yamaha-1.jpeg';
import office_yamaha2 from '@/assets/images/hicom-yamaha-2.jpeg';
import office_yamaha3 from '@/assets/images/hicom-yamaha-3.jpeg';
import office_yamaha4 from '@/assets/images/hicom-yamaha-4.jpeg';
import office_yamaha5 from '@/assets/images/hicom-yamaha-5.jpeg';

// Future Prelude SB 
import futureprelude1 from '@/assets/images/future-prelude-sb-1.jpeg';
import futureprelude2 from '@/assets/images/future-prelude-sb-2.jpeg';
import futureprelude3 from '@/assets/images/future-prelude-sb-3.jpeg';
import futureprelude4 from '@/assets/images/future-prelude-sb-4.jpeg';
import futureprelude5 from '@/assets/images/future-prelude-sb-5.jpeg';

// Agri Asia Sdn Bhd
import agriasia1 from '@/assets/images/agri-asia-1.jpeg';
import agriasia2 from '@/assets/images/agri-asia-2.jpeg';
import agriasia3 from '@/assets/images/agri-asia-3.jpeg';
import agriasia4 from '@/assets/images/agri-asia-4.jpeg';
import agriasia5 from '@/assets/images/agri-asia-5.jpeg';

// Petronas
import petronas1 from '@/assets/images/petronas-1.jpeg';
import petronas2 from '@/assets/images/petronas-2.jpeg';
import petronas3 from '@/assets/images/petronas-3.jpeg';
import petronas4 from '@/assets/images/petronas-4.jpeg';

// Proton
import proton1 from '@/assets/images/proton-1.jpeg';
import proton2 from '@/assets/images/proton-2.jpeg';
import proton3 from '@/assets/images/proton-3.jpeg';
import proton4 from '@/assets/images/proton-4.jpeg';

// Carls Jr
import carlsjr1 from '@/assets/images/carlsjr-1.jpeg';
import carlsjr2 from '@/assets/images/carlsjr-2.jpeg';
import carlsjr3 from '@/assets/images/carlsjr-3.jpeg';
import carlsjr4 from '@/assets/images/carlsjr-4.jpeg';

// Pest Control Images
import pestcontrol1 from '@/assets/images/pest.jpeg';
import pestcontrol2 from '@/assets/images/pest-2.jpeg';
import pestcontrol3 from '@/assets/images/pest-3.jpeg';
import pestcontrol4 from '@/assets/images/pest-4.jpeg';
import pestcontrol5 from '@/assets/images/pest-5.jpeg';
import pestcontrol6 from '@/assets/images/pest-6.jpeg';
import pestcontrol7 from '@/assets/images/pest-7.jpeg';
import pestcontrol8 from '@/assets/images/pest-8.jpeg';
import pestcontrol9 from '@/assets/images/pest-9.jpeg';
import pestcontrol10 from '@/assets/images/pest-10.jpeg';
import pestcontrol11 from '@/assets/images/pest-11.jpeg';
import pestcontrol12 from '@/assets/images/pest-12.jpeg';
import pestcontrol13 from '@/assets/images/pest-13.jpeg';
import pestcontrol14 from '@/assets/images/pest-14.jpeg';

// Define the interface for project gallery configuration
interface ProjectGalleryConfig {
  title: string;
  description: string;
  backgroundImage: string;
  location: string;
  images: {
    id: string;
    src: string;
    alt: string;
    description?: string;
  }[];
  backButtonText?: string;
  bookButtonText?: string;
}

// Define image mappings for each gallery
const galleryImageMappings: Record<string, Record<string, string[]>> = {
  'sanitise': {
    'hicom-yamaha': [office_yamaha1, office_yamaha2, office_yamaha3, office_yamaha4, office_yamaha5],
    'future-prelude-sb': [futureprelude1, futureprelude2, futureprelude3, futureprelude4, futureprelude5],
    'agri-asia': [agriasia1, agriasia2, agriasia3, agriasia4, agriasia5],
    'petronas': [petronas1, petronas2, petronas3, petronas4],
    'proton': [proton1, proton2, proton3, proton4],
    'carls-jr': [carlsjr1, carlsjr2, carlsjr3, carlsjr4]
  },
  'pest-control': {
    'residential-pest-control': [
      pestcontrol1, pestcontrol2, pestcontrol3, pestcontrol4, pestcontrol5,
      pestcontrol6, pestcontrol7, pestcontrol8, pestcontrol9, pestcontrol10,
      pestcontrol11, pestcontrol12, pestcontrol13, pestcontrol14
    ]
  }
};

// Build gallery data from translations
const buildProjectGalleries = (t: any): Record<string, Record<string, ProjectGalleryConfig>> => {
  const sanitiseGalleries = t('sanitiseGallery', { returnObjects: true }) as Array<{
    title: string;
    slug: string;
    description: string;
    location: string;
    imageDescriptions?: string[];
    backButtonText?: string;
    bookButtonText?: string;
  }>;

  const pestControlGalleries = t('pestControlGallery', { returnObjects: true }) as Array<{
    title: string;
    slug: string;
    description: string;
    location: string;
    imageDescriptions?: string[];
    backButtonText?: string;
    bookButtonText?: string;
  }>;

  const projectGalleries: Record<string, Record<string, ProjectGalleryConfig>> = {
    'sanitise': {},
    'pest-control': {}
  };

  // Build sanitise galleries
  sanitiseGalleries.forEach(gallery => {
    const images = galleryImageMappings['sanitise'][gallery.slug];
    if (images) {
      projectGalleries['sanitise'][gallery.slug] = {
        title: gallery.title,
        description: gallery.description,
        backgroundImage: images[0],
        location: gallery.location,
        backButtonText: gallery.backButtonText,
        bookButtonText: gallery.bookButtonText,
        images: images.map((img, index) => ({
          id: `${gallery.slug}-${index + 1}`,
          src: img,
          alt: `${gallery.title} - Image ${index + 1}`,
          description: gallery.imageDescriptions?.[index] || ''
        }))
      };
    }
  });

  // Build pest control galleries
  pestControlGalleries.forEach(gallery => {
    const images = galleryImageMappings['pest-control'][gallery.slug];
    if (images) {
      projectGalleries['pest-control'][gallery.slug] = {
        title: gallery.title,
        description: gallery.description,
        backgroundImage: images[0],
        location: gallery.location,
        backButtonText: gallery.backButtonText,
        bookButtonText: gallery.bookButtonText,
        images: images.map((img, index) => ({
          id: `${gallery.slug}-${index + 1}`,
          src: img,
          alt: `${gallery.title} - Image ${index + 1}`,
          description: gallery.imageDescriptions?.[index] || ''
        }))
      };
    }
  });

  return projectGalleries;
};

// Old hardcoded data - to be removed after refactoring
// @ts-expect-error - keeping for reference
const _oldProjectGalleries: Record<string, Record<string, ProjectGalleryConfig>> = {
  'sanitise': {
    'hicom-yamaha': {
      title: 'HICOM YAMAHA',
      description: 'Perkhidmatan sanitasi profesional untuk gudang HICOM YAMAHA. Kami memastikan persekitaran kerja yang bersih dan selamat untuk semua pekerja dengan menggunakan bahan kimia yang selamat dan berkesan.',
      backgroundImage: office_yamaha1,
      location: 'Shah Alam, Selangor',
      images: [
        {
          id: 'office-1',
          src: office_yamaha1,
          alt: 'HICOM YAMAHA warehouse sanitisation',
          description: 'Proses sanitasi di kawasan gudang utama'
        },
        {
          id: 'office-2',
          src: office_yamaha2,
          alt: 'HICOM YAMAHA warehouse sanitisation',
          description: 'Sanitasi menyeluruh di kawasan penyimpanan'
        },
        {
          id: 'office-3',
          src: office_yamaha3,
          alt: 'HICOM YAMAHA warehouse sanitisation',
          description: 'Pembersihan dan sanitasi di kawasan kerja'
        },
        {
          id: 'office-4',
          src: office_yamaha4,
          alt: 'HICOM YAMAHA warehouse sanitisation',
          description: 'Proses sanitasi di kawasan pejabat'
        },
        {
          id: 'office-5',
          src: office_yamaha5,
          alt: 'HICOM YAMAHA warehouse sanitisation',
          description: 'Sanitasi di kawasan laluan utama'
        }
      ]
    },
    'future-prelude-sb': {
      title: 'FUTURE PRELUDE SB',
      description: 'Perkhidmatan sanitasi profesional untuk Future Prelude SB. Kami memastikan persekitaran kerja yang bersih dan selamat untuk semua pekerja dengan menggunakan bahan kimia yang selamat dan berkesan.',
      backgroundImage: futureprelude1,
      location: 'Shah Alam, Selangor',
      images: [
        {
          id: 'future-prelude-1',
          src: futureprelude1,
          alt: 'Future Prelude SB sanitisation',
          description: 'Proses sanitasi di kawasan gudang utama'
        },
        {
          id: 'future-prelude-2',
          src: futureprelude2,
          alt: 'Future Prelude SB sanitisation',
          description: 'Sanitasi menyeluruh di kawasan penyimpanan'
        },
        {
          id: 'future-prelude-3',
          src: futureprelude3,
          alt: 'Future Prelude SB sanitisation',
          description: 'Pembersihan dan sanitasi di kawasan kerja'
        },
        {
          id: 'future-prelude-4',
          src: futureprelude4,
          alt: 'Future Prelude SB sanitisation',
          description: 'Proses sanitasi di kawasan pejabat'
        },
        {
          id: 'future-prelude-5',
          src: futureprelude5,
          alt: 'Future Prelude SB sanitisation',
          description: 'Sanitasi di kawasan laluan utama'
        }
      ]
    },
    'agri-asia': {
      title: 'AGRI ASIA SDN BHD',
      description: 'Perkhidmatan sanitasi profesional untuk Agri Asia SDN Bhd. Kami memastikan persekitaran kerja yang bersih dan selamat untuk semua pekerja dengan menggunakan bahan kimia yang selamat dan berkesan.',
      backgroundImage: agriasia1,
      location: 'Bangi, Selangor',
      images: [
        {
          id: 'agri-asia-1',
          src: agriasia1,
          alt: 'Agri Asia sanitisation',
          description: 'Proses sanitasi di kawasan gudang utama'
        },
        {
          id: 'agri-asia-2',
          src: agriasia2,
          alt: 'Agri Asia sanitisation',
          description: 'Sanitasi menyeluruh di kawasan penyimpanan'
        },
        {
          id: 'agri-asia-3',
          src: agriasia3,
          alt: 'Agri Asia sanitisation',
          description: 'Pembersihan dan sanitasi di kawasan kerja'
        },
        {
          id: 'agri-asia-4',
          src: agriasia4,
          alt: 'Agri Asia sanitisation',
          description: 'Proses sanitasi di kawasan pejabat'
        },
        {
          id: 'agri-asia-5',
          src: agriasia5,
          alt: 'Agri Asia sanitisation',
          description: 'Sanitasi di kawasan laluan utama'
        }
      ]
    },
    'petronas': {
      title: 'PETRONAS',
      description: 'Perkhidmatan sanitasi profesional untuk Petronas. Kami memastikan persekitaran kerja yang bersih dan selamat untuk semua pekerja dengan menggunakan bahan kimia yang selamat dan berkesan.',
      backgroundImage: petronas1,
      location: 'Nilai, Negeri Sembilan',
      images: [
        {
          id: 'petronas-1',
          src: petronas1,
          alt: 'Petronas sanitisation',
          description: 'Proses sanitasi di kawasan gudang utama'
        },
        {
          id: 'petronas-2',
          src: petronas2,
          alt: 'Petronas sanitisation',
          description: 'Sanitasi menyeluruh di kawasan penyimpanan'
        },
        {
          id: 'petronas-3',
          src: petronas3,
          alt: 'Petronas sanitisation',
          description: 'Pembersihan dan sanitasi di kawasan kerja'
        },
        {
          id: 'petronas-4',
          src: petronas4,
          alt: 'Petronas sanitisation',
          description: 'Proses sanitasi di kawasan pejabat'
        }
      ]
    },
    'proton': {
      title: 'PROTON',
      description: 'Perkhidmatan sanitasi profesional untuk Proton. Kami memastikan persekitaran kerja yang bersih dan selamat untuk semua pekerja dengan menggunakan bahan kimia yang selamat dan berkesan.',
      backgroundImage: proton1,
      location: 'Proton City, Selangor',  
      images: [
        {
          id: 'proton-1',
          src: proton1,
          alt: 'Proton sanitisation',
          description: 'Proses sanitasi di kawasan gudang utama'
        },
        {
          id: 'proton-2',
          src: proton2,
          alt: 'Proton sanitisation',
          description: 'Sanitasi menyeluruh di kawasan penyimpanan'
        },
        {
          id: 'proton-3',
          src: proton3,
          alt: 'Proton sanitisation',
          description: 'Pembersihan dan sanitasi di kawasan kerja'
        },
        {
          id: 'proton-4',
          src: proton4,
          alt: 'Proton sanitisation',
          description: 'Proses sanitasi di kawasan pejabat'
        }
      ]
    },
    'carls-jr': {
      title: 'CARL\'S JR.',
      description: 'Perkhidmatan sanitasi profesional untuk Carls Jr. Kami memastikan persekitaran kerja yang bersih dan selamat untuk semua pekerja dengan menggunakan bahan kimia yang selamat dan berkesan.',
      backgroundImage: carlsjr1,
      location: 'Setia Alam, Selangor',
      images: [
        {
          id: 'carlsjr-1',
          src: carlsjr1,
          alt: 'Carls Jr sanitisation',
          description: 'Proses sanitasi di kawasan gudang utama'
        },
        {
          id: 'carlsjr-2',
          src: carlsjr2,
          alt: 'Carls Jr sanitisation',
          description: 'Sanitasi menyeluruh di kawasan penyimpanan'
        },
        {
          id: 'carlsjr-3',
          src: carlsjr3,
          alt: 'Carls Jr sanitisation',
          description: 'Pembersihan dan sanitasi di kawasan kerja'
        },
        {
          id: 'carlsjr-4',
          src: carlsjr4,
          alt: 'Carls Jr sanitisation',
          description: 'Proses sanitasi di kawasan pejabat'
        }
      ]
    },
},
    'pest-control': {
      'residential-pest-control': {
        title: 'Kawalan Serangga Kediaman',
      description: 'Perkhidmatan kawalan serangga profesional untuk kawasan kediaman. Kami memastikan rumah anda bebas daripada serangga perosak dengan menggunakan kaedah yang selamat dan berkesan.',
      backgroundImage: pestcontrol1,
      location: 'Lokasi: Selangor dan KL',
      images: [
        {
          id: 'pest-1',
          src: pestcontrol1,
          alt: 'Kawalan serangga kediaman',
          description: 'Rawatan kawalan serangga di kawasan rumah'
        },
        {
          id: 'pest-2',
          src: pestcontrol2,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan dan rawatan di sudut tersembunyi'
        },
        {
          id: 'pest-3',
          src: pestcontrol3,
          alt: 'Kawalan serangga kediaman',
          description: 'Rawatan pencegahan serangga'
        },
        {
          id: 'pest-4',
          src: pestcontrol4,
          alt: 'Kawalan serangga kediaman',
          description: 'Kawalan serangga di kawasan dapur'
        },
        {
          id: 'pest-5',
          src: pestcontrol5,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-6',
          src: pestcontrol6,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-7',
          src: pestcontrol7,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-8',
          src: pestcontrol8,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-9',
          src: pestcontrol9,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-10',
          src: pestcontrol10,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-11',
          src: pestcontrol11,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-12',
          src: pestcontrol12,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-13',
          src: pestcontrol13,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        },
        {
          id: 'pest-14',
          src: pestcontrol14,
          alt: 'Kawalan serangga kediaman',
          description: 'Pemeriksaan kawasan berisiko tinggi'
        }
      ]
    }
  }
};

const ProjectGalleryPage = () => {
  const { serviceType, slug } = useParams<{ serviceType: string; slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  // Build galleries from translations
  const projectGalleries = buildProjectGalleries(t);
  const gallery = serviceType && slug ? projectGalleries[serviceType]?.[slug] : null;

  const handleBookingClick = () => {
    setIsBookingOpen(true);
  };

  const handleBackClick = () => {
    navigate(`/services/${serviceType}`);
    setTimeout(() => {
      const gallerySection = document.getElementById(`${serviceType}-gallery`);
      if (gallerySection) {
        gallerySection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (!gallery) {
    const backButtonText = serviceType === 'sanitise'
      ? t('servicePage.sanitiseGalleryTitle')
      : t('servicePage.pestControlGalleryTitle');

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('common.galleryNotFound', 'Gallery not found')}</h2>
          <button
            onClick={handleBackClick}
            className={`${serviceType === 'sanitise' ? 'bg-trhine-green-100' : 'bg-trhine-brown-100'} text-white px-4 py-2 rounded-md`}
          >
            ← {backButtonText}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${gallery.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="min-h-[50vh] flex flex-col justify-center relative"
      >
        <div className="container mx-auto px-4 py-16">
          <button
            onClick={handleBackClick}
            className="text-white mb-4 flex items-center pt-14"
          >
            <span className="mr-2">←</span>
            {gallery.backButtonText || t('servicePage.sanitiseGalleryTitle')}
          </button>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">{gallery.title}</h1>
          <p className="text-lg text-white max-w-3xl mb-4">{gallery.description}</p>
          <p className="text-lg text-white max-w-3xl mb-8">{gallery.location}</p>
          <Button
            text={gallery.bookButtonText || t('common.bookNow')}
            active={true}
            className={serviceType === 'sanitise' ? 'bg-trhine-green-100' : 'bg-trhine-brown-100'}
            onClick={handleBookingClick}
            serviceId={serviceType}
          />
        </div>
      </section>
      
      {/* Gallery Section */}
      {gallery.images.length > 0 && (
        <ServiceGallery 
          images={gallery.images}
          title={`Gallery of ${gallery.title}`}
          layout="masonry"
        />
      )}
      
      <Booking 
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        serviceId={serviceType || ''}
        selectedServices={[]}
      />
      
      <Footer 
        footer_bg={serviceType === 'sanitise' ? 'bg-trhine-green-100' : 'bg-trhine-brown-100'}
        text_color="text-white"
      />
    </div>
  );
};

export default ProjectGalleryPage; 